// src/pages/BajasEstadisticas.tsx
import React, { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth, getAuthToken } from "../security/AuthLogin";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AmountDropdown from "../components/amountDropdown";
import LoadingLottie from "../components/LoadingLottie";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faLink,
  faLandmark,
  faMapMarkerAlt,
  faUser,
  faFileExcel,
  faSpinner,
  faCheck,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";

import {
  fetchBajasEstadisticas,
  BajaEstadistica,
} from "../api/bajasEstadisticasApi";

import "../css/concursos.css";
import "../css/checkboxdropdown.css";
import "../css/amountdropdown.css";

/* ───────────── TIPOS ───────────── */
interface IBajaEstadisticaConMensajes extends BajaEstadistica {
  leido?: number;
  ultimo_emisor?: string | null;
  ultima_fecha_msg?: string | null;
  fecha_fin_ofertas?: string | null;
  dueno_nombre?: string | null;
}

/* ───────────── HELPERS ───────────── */
const mapEstadoSoporte = (estado: string | null | undefined): string => {
  if (!estado || estado.toLowerCase() === "pendiente") return "En estudio";
  switch (estado.toLowerCase()) {
    case "valido":
      return "Viable";
    case "rechazado":
      return "No viable";
    case "oculto":
      return "Oculto";
    case "norecomendado":
      return "No recomendado";
    default:
      return estado.charAt(0).toUpperCase() + estado.slice(1);
  }
};

const slugSoporte = (estado: string | null | undefined): string => {
  if (!estado || estado.toLowerCase() === "pendiente") return "en-estudio";
  switch (estado.toLowerCase()) {
    case "valido":
      return "viable";
    case "rechazado":
      return "no-viable";
    case "oculto":
      return "oculto";
    case "norecomendado":
      return "no-recomendado";
    default:
      return estado.toLowerCase().replace(/ /g, "-");
  }
};

const getEmisorPriority = (
  baja: IBajaEstadisticaConMensajes,
  role: string
): number => {
  if (!baja.ultimo_emisor) return 4;
  if (role === "cliente") {
    if (baja.ultimo_emisor === "nadie") return 1;
    if (baja.ultimo_emisor === "admin_tecnico") return 2;
    return 3;
  } else {
    if (baja.ultimo_emisor === "nadie") return 1;
    if (baja.ultimo_emisor === "cliente") return 2;
    return 3;
  }
};

const getBackgroundColor = (
  baja: IBajaEstadisticaConMensajes,
  role: string
): string => {
  if (
    (role === "admin" || role === "tecnico") &&
    baja.estado_soporte_favorito?.toLowerCase() === "oculto"
  )
    return "black";

  const leidoVal = Number(baja.leido) || 0;

  if (role === "cliente") {
    if (baja.ultimo_emisor === "cliente") return "rgb(255,0,0)";
    if (baja.ultimo_emisor === "admin_tecnico")
      return leidoVal === 0 ? "rgb(255,255,0)" : "white";
    return "rgb(255,0,0)";
  } else {
    if (baja.ultimo_emisor === "nadie" || baja.ultimo_emisor === "cliente")
      return "rgb(255,0,0)";
    if (baja.ultimo_emisor === "admin_tecnico") return "rgb(0,255,0)";
    return "white";
  }
};

/* ───────────── COMPONENTE ───────────── */
const BajasEstadisticas: React.FC = () => {
  const { userRole } = useAuth();

  const [bajas, setBajas] = useState<IBajaEstadisticaConMensajes[] | null>(
    null
  );
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [viewMode, setViewMode] = useState<"abiertos" | "cerrados">("abiertos");

  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") || "1", 10);
  const limit = parseInt(searchParams.get("limit") || "10", 10);
  const maxPages = Math.max(Math.ceil(total / limit), 1);

  /* filtros locales */
  const [localSearch, setLocalSearch] = useState("");
  const [localFechaInicio, setLocalFechaInicio] = useState("");
  const [localFechaFin, setLocalFechaFin] = useState("");
  const [localImporteDesde, setLocalImporteDesde] = useState("");
  const [localImporteHasta, setLocalImporteHasta] = useState("");
  const [localAutor, setLocalAutor] = useState("");
  const [localAsignado, setLocalAsignado] = useState("");

  const [localNoRespondidos, setLocalNoRespondidos] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  const [excelStatus, setExcelStatus] = useState<
    "idle" | "loading" | "success"
  >("idle");

  /* utils */
  const cleanNum = (n: string) =>
    n ? n.replace(/\./g, "").replace(",", ".") : "";

  /* ---- query helpers ---- */
  const loadBajas = async () => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setBajas(null);
    setLoading(true);

    try {
      const qp = (k: string) => searchParams.get(k);
      const params: any = { page, limit };

      if (qp("search")) params.search = qp("search");
      if (qp("fechaInicio")) params.fechaInicio = qp("fechaInicio");
      if (qp("fechaFin")) params.fechaFin = qp("fechaFin");
      if (qp("importeDesde")) params.importeDesde = qp("importeDesde");
      if (qp("importeHasta")) params.importeHasta = qp("importeHasta");
      if ((userRole === "admin" || userRole === "tecnico") && qp("autor"))
        params.autor = qp("autor");
      if ((userRole === "admin" || userRole === "tecnico") && qp("asignado"))
        params.asignado = qp("asignado");
      if (qp("cerrados")) params.cerrados = qp("cerrados");
      if (
        (userRole === "admin" || userRole === "tecnico") &&
        qp("noRespondidos")
      )
        params.noRespondidos = qp("noRespondidos");

      const { data, total } = await fetchBajasEstadisticas(
        params,
        abortRef.current.signal
      );
      setBajas(data as IBajaEstadisticaConMensajes[]);
      setTotal(total);
    } catch (e: any) {
      if (e.name !== "AbortError") {
        console.error(e);
        alert(`Error al obtener bajas: ${e.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  /* ---- useEffect inicial ---- */
  useEffect(() => {
    if (!searchParams.get("cerrados")) {
      const p = new URLSearchParams(searchParams);
      p.set("cerrados", "false");
      p.set("page", "1");
      setSearchParams(p);
    } else {
      loadBajas();
    }
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /* ---- pagination helpers ---- */
  const changePage = (n: number) => {
    const p = new URLSearchParams(searchParams);
    p.set("page", String(n));
    setSearchParams(p);
  };

  /* ---- filter handlers ---- */
  const applyFilters = () => {
    const p = new URLSearchParams();
    if (localSearch) p.set("search", localSearch);
    if (localFechaInicio) p.set("fechaInicio", localFechaInicio);
    if (localFechaFin) p.set("fechaFin", localFechaFin);
    if (localImporteDesde) p.set("importeDesde", cleanNum(localImporteDesde));
    if (localImporteHasta) p.set("importeHasta", cleanNum(localImporteHasta));
    if ((userRole === "admin" || userRole === "tecnico") && localAutor)
      p.set("autor", localAutor);
    if ((userRole === "admin" || userRole === "tecnico") && localAsignado)
      p.set("asignado", localAsignado);
    if ((userRole === "admin" || userRole === "tecnico") && localNoRespondidos)
      p.set("noRespondidos", "true");
    p.set("cerrados", viewMode === "cerrados" ? "true" : "false");
    p.set("page", "1");
    setSearchParams(p);
  };

  const resetFilters = () => {
    setLocalSearch("");
    setLocalFechaInicio("");
    setLocalFechaFin("");
    setLocalImporteDesde("");
    setLocalImporteHasta("");
    setLocalAutor("");
    setLocalAsignado("");
    setLocalNoRespondidos(false);
    const p = new URLSearchParams();
    p.set("cerrados", viewMode === "cerrados" ? "true" : "false");
    p.set("page", "1");
    setSearchParams(p);
  };

  /* ---- excel ---- */
  const downloadExcel = async () => {
    try {
      setExcelStatus("loading");
      const token = getAuthToken();
      if (!token) {
        alert("Token no encontrado. Inicia sesión.");
        setExcelStatus("idle");
        return;
      }
      const url = `${window.location.protocol}//${window.location.hostname}/api/bajas-estadisticas/export`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(resp.statusText);
      const blob = await resp.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = "bajas_estadisticas.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
      setExcelStatus("success");
      setTimeout(() => setExcelStatus("idle"), 2000);
    } catch (e) {
      console.error(e);
      alert("Error al exportar Excel.");
      setExcelStatus("idle");
    }
  };

  const excelBtnContent =
    excelStatus === "loading" ? (
      <>
        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 5 }} />
        Generando Excel...
      </>
    ) : excelStatus === "success" ? (
      <>
        <FontAwesomeIcon icon={faCheck} style={{ marginRight: 5 }} />
        ¡Descargado!
      </>
    ) : (
      <>
        <FontAwesomeIcon icon={faFileExcel} style={{ marginRight: 5 }} />
        Descargar Excel
      </>
    );

  const formatearFecha = (fechaStr: string) => {
    if (!fechaStr) return "No hay fecha límite";

    try {
      const fecha = new Date(fechaStr);

      if (isNaN(fecha.getTime())) {
        throw new Error("Fecha inválida");
      }

      const day = fecha.getUTCDate().toString().padStart(2, "0");
      const month = (fecha.getUTCMonth() + 1).toString().padStart(2, "0");
      const year = fecha.getUTCFullYear();
      const hours = fecha.getUTCHours().toString().padStart(2, "0");
      const minutes = fecha.getUTCMinutes().toString().padStart(2, "0");

      return `${day}/${month}/${year} a las ${hours}:${minutes}h`;
    } catch (error) {
      console.error(
        "Error formateando fecha:",
        error,
        "Fecha recibida:",
        fechaStr
      );
      return "Fecha inválida";
    }
  };

  /* ---- lista final para mostrar ---- */
  const visibles =
    userRole === "cliente"
      ? (bajas || []).filter(
          (b) => b.estado_soporte_favorito?.toLowerCase() !== "oculto"
        )
      : bajas || [];

  const ordered = [...visibles].sort((a, b) => {
    const pa = getEmisorPriority(a, userRole || "");
    const pb = getEmisorPriority(b, userRole || "");
    if (pa !== pb) return pa - pb;
    const da = a.ultima_fecha_msg ? new Date(a.ultima_fecha_msg).getTime() : 0;
    const db = b.ultima_fecha_msg ? new Date(b.ultima_fecha_msg).getTime() : 0;
    return db - da;
  });

  /* ───────────── RENDER ───────────── */
  return (
    <div className="container-concursos">
      <h2>Bajas Estadísticas</h2>

      {/* Tabs */}
      <div style={{ marginBottom: "1rem" }}>
        <button
          onClick={() => {
            setViewMode("abiertos");
            const p = new URLSearchParams(searchParams);
            p.set("cerrados", "false");
            p.set("page", "1");
            setSearchParams(p);
          }}
          style={{
            marginRight: "1rem",
            backgroundColor: viewMode === "abiertos" ? "#b90029" : "#555",
            color: "#fff",
          }}
        >
          Bajas Abiertas
        </button>
        <button
          onClick={() => {
            setViewMode("cerrados");
            const p = new URLSearchParams(searchParams);
            p.set("cerrados", "true");
            p.set("page", "1");
            setSearchParams(p);
          }}
          style={{
            backgroundColor: viewMode === "cerrados" ? "#b90029" : "#555",
            color: "#fff",
          }}
        >
          Cerradas
        </button>
      </div>

      {/* Filtros */}
      <div className="filtros">
        <input
          className="form-control busqueda"
          placeholder="Buscar (título/expediente)..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              applyFilters();
            }
          }}
        />
        {(userRole === "admin" || userRole === "tecnico") && (
          <input
            className="form-control busqueda"
            placeholder="Filtrar por cliente..."
            value={localAutor}
            onChange={(e) => setLocalAutor(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyFilters();
              }
            }}
          />
        )}
        {(userRole === "admin" || userRole === "tecnico") && (
          <input
            className="form-control busqueda"
            placeholder="Filtrar por persona asignada..."
            value={localAsignado}
            onChange={(e) => setLocalAsignado(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyFilters();
              }
            }}
          />
        )}
        <AmountDropdown
          desde={localImporteDesde}
          hasta={localImporteHasta}
          onChange={(d, h) => {
            setLocalImporteDesde(d);
            setLocalImporteHasta(h);
          }}
          placeholder="Importe del concurso"
        />
        <DatePicker
          selected={localFechaInicio ? new Date(localFechaInicio) : undefined}
          onChange={(dates: [Date | null, Date | null]) => {
            const [s, e] = dates;
            if (s) {
              const iso = new Date(
                s.getTime() - s.getTimezoneOffset() * 60000
              ).toISOString();
              setLocalFechaInicio(iso.split("T")[0]);
            } else setLocalFechaInicio("");
            if (e) {
              const iso = new Date(
                e.getTime() - e.getTimezoneOffset() * 60000
              ).toISOString();
              setLocalFechaFin(iso.split("T")[0]);
            } else setLocalFechaFin("");
          }}
          startDate={localFechaInicio ? new Date(localFechaInicio) : undefined}
          endDate={localFechaFin ? new Date(localFechaFin) : undefined}
          selectsRange
          isClearable
          dateFormat="dd/MM/yyyy"
          placeholderText="Fecha de alta"
        />
        {(userRole === "admin" || userRole === "tecnico") && (
          <div className="checkbox-filtro">
            <label>
              <input
                type="checkbox"
                checked={localNoRespondidos}
                onChange={(e) => setLocalNoRespondidos(e.target.checked)}
              />
              No respondidos
            </label>
          </div>
        )}
        <button
          className="btn btn-primary"
          onClick={applyFilters}
          disabled={loading}
          onKeyDown={(e) => {
            if (e.key === "Enter") applyFilters();
          }}
        >
          Filtrar
        </button>
        <button
          className="btn btn-secondary"
          onClick={resetFilters}
          disabled={loading}
        >
          Resetear
        </button>
      </div>

      {/* Excel */}
      {(userRole === "admin" || userRole === "tecnico") && (
        <div style={{ margin: "1rem 0" }}>
          <button
            className="btn btn-success"
            onClick={downloadExcel}
            disabled={excelStatus === "loading"}
          >
            {excelBtnContent}
          </button>
        </div>
      )}

      {/* Listado */}
      <div className="listing-container" style={{ marginTop: "1rem" }}>
        {loading || bajas === null ? (
          <LoadingLottie />
        ) : ordered.length === 0 ? (
          <p>No hay bajas estadísticas.</p>
        ) : (
          ordered.map((baja, idx) => {
            const importe =
              (baja.importe_total || 0) > 0
                ? baja.importe_total
                : baja.importe_estimado || 0;
            const importeTxt =
              (importe ?? 0) > 0
                ? new Intl.NumberFormat("es-ES", {
                    style: "currency",
                    currency: "EUR",
                    minimumFractionDigits: (importe || 0) % 1 === 0 ? 0 : 2,
                  }).format(importe || 0)
                : "N/A";

            const estadoVisible = mapEstadoSoporte(
              baja.estado_soporte_favorito
            );
            const estadoSlug = slugSoporte(baja.estado_soporte_favorito);
            const glow = getBackgroundColor(baja, userRole || "");

            return (
              <div
                key={`${baja.id_concurso}-${idx}`}
                className="listing-item-wrapper"
                style={{ boxShadow: `0 0 15px 2px ${glow}` }}
              >
                <div className="listing-item">
                  <div className="info">
                    {/* Cabecera */}
                    <div className="header-concurso">
                      <div className="expediente">{baja.codigo_expediente}</div>
                      <div className="subheader-concurso">
                        <div
                          className={`estado estado-${estadoSlug}`}
                          title="Estado del soporte"
                        >
                          {estadoVisible}
                        </div>
                        <div className="publication-date">
                          {baja.fecha_publicacion
                            ? new Date(
                                baja.fecha_publicacion
                              ).toLocaleDateString()
                            : ""}
                        </div>
                      </div>
                    </div>

                    {/* Título + detalles */}
                    <div className="title">{baja.titulo}</div>
                    <div className="details">
                      <div>
                        <FontAwesomeIcon icon={faLandmark} className="icon" />{" "}
                        {baja.entidad_compradora}
                      </div>
                      <div>
                        <FontAwesomeIcon
                          icon={faMapMarkerAlt}
                          className="icon"
                        />{" "}
                        {baja.pais}
                        {baja.localidad ? ` - ${baja.localidad}` : ""}
                      </div>
                    </div>

                    <div className="extra-info">
                      <div className="fin-plazo">
                        {baja.fecha_fin_ofertas ? (
                          <>
                            <span>Hasta&nbsp;</span>
                            <span>
                              {formatearFecha(baja.fecha_fin_ofertas)}
                            </span>
                          </>
                        ) : (
                          <span>Fecha límite no disponible</span>
                        )}
                      </div>
                    </div>

                    {/* Cliente (solo admin/tec) */}
                    {(userRole === "admin" || userRole === "tecnico") &&
                      baja.cliente_nombre && (
                        <div style={{ marginTop: ".8rem" }}>
                          <FontAwesomeIcon
                            icon={faUser}
                            style={{ marginRight: ".4rem" }}
                          />
                          <strong>Cliente:</strong>{" "}
                          <em>{baja.cliente_nombre}</em>
                        </div>
                      )}

                    {/* Fecha de alta */}
                    {baja.fecha_alta_baja && (
                      <div
                        style={{
                          marginTop: ".8rem",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faCalendarAlt}
                          style={{ marginRight: ".4rem" }}
                        />
                        <strong>Fecha alta:</strong>{" "}
                        <em>
                          {new Date(baja.fecha_alta_baja).toLocaleDateString(
                            "es-ES",
                            {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            }
                          )}{" "}
                          a las{" "}
                          {new Date(baja.fecha_alta_baja).toLocaleTimeString(
                            "es-ES",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </em>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="footer-concurso">
                    <div className="importe">{importeTxt}</div>
                    <div className="actions-concurso">
                      {(userRole === "admin" || userRole === "tecnico") && (
                        <div
                          style={{
                            marginRight: "0.5rem",
                            padding: "0.25rem 0.75rem",
                            borderRadius: "12px",
                            backgroundColor: "rgba(0, 123, 255, 0.15)", // color azul suave y semitransparente
                            fontSize: "0.875rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Asignado a {baja.dueno_nombre ?? "nadie"}
                        </div>
                      )}
                      <Link
                        to={`/baja-estadistica/${baja.id_concurso}/${baja.id_usuario}`}
                      >
                        <FontAwesomeIcon icon={faEye} title="Ver detalle" />
                      </Link>
                      {baja.enlace && (
                        <a
                          href={baja.enlace}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ marginLeft: ".5rem" }}
                        >
                          <FontAwesomeIcon
                            icon={faLink}
                            title="Enlace oficial"
                          />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Paginación */}
      <div className="pagination-concursos" style={{ marginTop: "1rem" }}>
        <button onClick={() => changePage(page - 1)} disabled={page <= 1}>
          Anterior
        </button>
        <span>
          Página {page} de {maxPages}
        </span>
        <button
          onClick={() => changePage(page + 1)}
          disabled={page >= maxPages}
        >
          Siguiente
        </button>
      </div>
    </div>
  );
};

export default BajasEstadisticas;
